import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RefreshCw, DownloadCloud, TrendingUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { xeroFinancialsAPI, FINANCIALS_ENABLED } from '../../lib/api';

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-AU'); } catch { return iso; }
};

// One report rendered as a simple label/value table.
function ReportCard({ report }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" /> {report.report_name}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {report.report_title} · imported {fmt(report.imported_at)}
        </p>
      </CardHeader>
      <CardContent>
        {(!report.rows || report.rows.length === 0) ? (
          <p className="text-sm text-muted-foreground/70">No line items returned.</p>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {report.rows.map((r, i) => (
                r.is_header ? (
                  <tr key={i}><td colSpan={2} className="pt-3 pb-1 font-semibold text-foreground/90">{r.label}</td></tr>
                ) : (
                  <tr key={i} className="border-b border-border/60">
                    <td className="py-1.5 text-muted-foreground">{r.label}</td>
                    <td className="py-1.5 text-right font-mono text-foreground">{(r.values || []).join('  ')}</td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

export function FinancialsTab({ clientId }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [source, setSource] = useState(null);

  useEffect(() => {
    if (FINANCIALS_ENABLED) load();
    else setLoading(false);
    /* eslint-disable-next-line */
  }, [clientId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await xeroFinancialsAPI.list(clientId);
      setReports(res.data.reports || []);
      if (res.data.reports?.[0]) setSource(res.data.reports[0].source_tenant_name);
    } catch {
      // empty / not permitted — handled by the empty state
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await xeroFinancialsAPI.importForClient(clientId);
      setSource(res.data.source);
      toast.success(`Imported ${res.data.imported.length} reports from ${res.data.source}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import financials', { duration: 8000 });
    } finally {
      setImporting(false);
    }
  };

  if (!FINANCIALS_ENABLED) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <TrendingUp className="w-14 h-14 mx-auto mb-3 text-muted-foreground/50" />
          <p className="text-muted-foreground">Financial import is temporarily disabled.</p>
          <p className="text-sm text-muted-foreground/70 mt-1">
            Paused while client sync moves to Xero Practice Manager — the feature can be switched back on later.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Financials from Xero</h3>
          {source && (
            <p className="text-xs text-muted-foreground">
              Source: <Badge variant="outline" className="ml-1">{source}</Badge>
            </p>
          )}
        </div>
        <Button onClick={handleImport} disabled={importing} className="bg-primary hover:bg-primary/90" data-testid="import-financials-button">
          {reports.length > 0
            ? <><RefreshCw className={`w-4 h-4 mr-2 ${importing ? 'animate-spin' : ''}`} /> {importing ? 'Importing…' : 'Refresh'}</>
            : <><DownloadCloud className="w-4 h-4 mr-2" /> {importing ? 'Importing…' : 'Import from Xero'}</>}
        </Button>
      </div>

      {loading ? (
        <div className="animate-pulse h-40 bg-muted rounded" />
      ) : reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DownloadCloud className="w-14 h-14 mx-auto mb-3 text-muted-foreground/50" />
            <p className="text-muted-foreground">No financials imported yet.</p>
            <p className="text-sm text-muted-foreground/70 mt-1">
              Click "Import from Xero" to pull this client's Profit &amp; Loss, Balance Sheet, and Trial Balance.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            Figures are read directly from the connected Xero organisation ({source}). Re-import to refresh.
          </div>
          {reports.map((r) => <ReportCard key={r.id} report={r} />)}
        </div>
      )}
    </div>
  );
}
